import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingService } from '../../services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { SeoService } from '../../services/meta.service';
import { User } from '../../common/interfaces/user.interface';
import { WirePhoneInputComponent } from '../../common/components/wire-phone-input/wire-phone-input.component';
import { phoneValidator } from '../../common/validators/form.validator';

@Component({
  selector: 'wire-phone-login',
  templateUrl: './phone-login.component.html',
  styleUrls: ['./phone-login.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    WirePhoneInputComponent
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneLoginComponent {

  private readonly userService = inject(UserService);
  private readonly loadingService = inject(LoadingService);
  private readonly toastr = inject(ToastrService);
  private readonly seoService = inject(SeoService);
  private readonly router = inject(Router);
  public destroyRef = inject(DestroyRef);
  public cdr = inject(ChangeDetectorRef);

  public loading = false;
  public otpSent = false;

  public phoneForm = new FormGroup({
    phone: new FormGroup({
      countryCode: new FormControl('+1', [Validators.required]),
      mobile: new FormControl('', [Validators.required, phoneValidator])
    })
  });

  public initialValue = {
    countryCode: '+1',
    mobile: ''
  };

  public otpCode = new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')]);

  public otpForm = new FormGroup({
    code: this.otpCode,
  });

  constructor() {
    this.seoService.setMetaTags({
      title: 'Wire Festival | Phone Login',
      description: 'Wire Festival Phone Login',
      keywords: 'Wire Festival Phone Login'
    });
  }

  handlePhoneNumberChange(event: string): void {
    this.phoneForm.get('phone')?.get('mobile')?.patchValue(event);
  }

  handleCountryChange(event: string): void {
    this.phoneForm.get('phone')?.get('countryCode')?.setValue(event);
  }

  sendOtp(): void {
    this.loading = true;
    const countryCode = this.phoneForm.value.phone?.countryCode!;
    const phone = this.phoneForm.value.phone?.mobile!;
    this.userService.sendOtp(countryCode, phone)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false;
          this.otpSent = true;
          this.toastr.success('OTP code sent to your phone');
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.toastr.error(error.error?.message || 'Failed to send OTP');
          this.cdr.detectChanges();
        }
      });
  }

  verifyOtp(): void {
    this.loading = true;
    const countryCode = this.phoneForm.value.phone?.countryCode!;
    const phone = this.phoneForm.value.phone?.mobile!;
    this.userService.verifyOtp(
      countryCode,
      phone,
      this.otpCode.value!
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { access_token: string, user: User }) => {
          this.loading = false;
          if (response.access_token && response.user) {
            this.loadingService.show();
            setTimeout(() => this.loadingService.hide(), 3000);
            this.updateUserEvents();

            if (response.user.isProfileCompleted) {
              this.router.navigate(['/profile/details']);
            } else {
              this.router.navigate(['/profile/user/personal']);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.toastr.error(error.error?.message || 'Invalid OTP code');
          this.cdr.detectChanges();
        }
      });
  }

  resendOtp(): void {
    this.sendOtp();
  }

  editPhone(): void {
    this.otpSent = false;
    this.otpCode.reset();
    this.initialValue = {
      countryCode: this.phoneForm.value.phone?.countryCode || '+1',
      mobile: this.phoneForm.value.phone?.mobile || ''
    };
    this.cdr.detectChanges();
  }

  updateUserEvents(): void {
    this.userService.getMyGoings().subscribe();
  }
}
